import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Avatar,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Science,
  Download,
  Visibility,
  Bloodtype,
  LocalHospital,
  Image,
  Description,
  FilterList,
  Refresh
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import SectionHeader from '@shared/SectionHeader';
import FilterBar, { FilterOption } from '@shared/FilterBar';
import PaginatedList from '@shared/PaginatedList';
import EmptyState from '@shared/EmptyState';
// import LabCard from '../components/labs/LabCard';

export interface LabDocument {
  id: string;
  animalId: string;
  animalName: string;
  type: 'sange' | 'urina' | 'eco' | 'radiografie' | 'altul';
  title: string;
  description?: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  labProvider?: string;
  labDate: string;
  createdAt: string;
  downloadUrl?: string;
  previewUrl?: string;
}

const LabsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [documents, setDocuments] = useState<LabDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Paginare
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 12;

  // Filtre
  const [filters, setFilters] = useState({
    animalId: '',
    type: '',
    fromDate: '',
    toDate: '',
    search: ''
  });

  const filterOptions: FilterOption[] = [
    {
      key: 'animalId',
      label: 'Animal',
      type: 'select',
      options: [
        { value: '', label: 'Toate animalele' },
        { value: 'animal1', label: 'Rex' },
        { value: 'animal2', label: 'Whiskers' }
      ]
    },
    {
      key: 'type',
      label: 'Tip analiză',
      type: 'select',
      options: [
        { value: '', label: 'Toate tipurile' },
        { value: 'sange', label: 'Analize de sânge' },
        { value: 'urina', label: 'Analize de urină' },
        { value: 'eco', label: 'Ecografie' },
        { value: 'radiografie', label: 'Radiografie' },
        { value: 'altul', label: 'Altele' }
      ]
    },
    {
      key: 'fromDate',
      label: 'De la data',
      type: 'date'
    },
    {
      key: 'toDate',
      label: 'Până la data',
      type: 'date'
    },
    {
      key: 'search',
      label: 'Caută',
      type: 'text',
      placeholder: 'Caută în documente...'
    }
  ];

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Loading lab documents...');
      
      // TODO: Înlocuiește cu API real
      const mockDocuments: LabDocument[] = [
        {
          id: '1',
          animalId: 'animal1',
          animalName: 'Rex',
          type: 'sange',
          title: 'Analize complete de sânge',
          description: 'Hemogramă completă, biochimie, funcții hepatice și renale',
          fileName: 'analize_sange_rex_2024.pdf',
          fileSize: 2048576, // 2MB
          mimeType: 'application/pdf',
          labProvider: 'Laborator Veterinar Central',
          labDate: '2024-10-15',
          createdAt: '2024-10-15T10:30:00Z'
        },
        {
          id: '2',
          animalId: 'animal1',
          animalName: 'Rex',
          type: 'eco',
          title: 'Ecografie abdominală',
          description: 'Ecografie completă a organelor abdominale',
          fileName: 'eco_abdominal_rex_2024.jpg',
          fileSize: 1536000, // 1.5MB
          mimeType: 'image/jpeg',
          labProvider: 'Clinica Veterinară Modernă',
          labDate: '2024-10-10',
          createdAt: '2024-10-10T14:20:00Z'
        },
        {
          id: '3',
          animalId: 'animal2',
          animalName: 'Whiskers',
          type: 'urina',
          title: 'Analize de urină',
          description: 'Examen complet de urină cu sediment',
          fileName: 'analize_urina_whiskers_2024.pdf',
          fileSize: 1024000, // 1MB
          mimeType: 'application/pdf',
          labProvider: 'Laborator Veterinar Central',
          labDate: '2024-10-05',
          createdAt: '2024-10-05T09:15:00Z'
        }
      ];

      // Filtrare mock
      let filtered = mockDocuments;
      
      if (filters.animalId) {
        filtered = filtered.filter(d => d.animalId === filters.animalId);
      }
      if (filters.type) {
        filtered = filtered.filter(d => d.type === filters.type);
      }
      if (filters.fromDate) {
        filtered = filtered.filter(d => d.labDate >= filters.fromDate);
      }
      if (filters.toDate) {
        filtered = filtered.filter(d => d.labDate <= filters.toDate);
      }
      if (filters.search) {
        filtered = filtered.filter(d => 
          d.title.toLowerCase().includes(filters.search.toLowerCase()) ||
          d.description?.toLowerCase().includes(filters.search.toLowerCase()) ||
          d.fileName.toLowerCase().includes(filters.search.toLowerCase())
        );
      }

      setDocuments(filtered);
      setTotalItems(filtered.length);
      setTotalPages(Math.ceil(filtered.length / itemsPerPage));
      
      console.log('✅ Lab documents loaded:', filtered.length);
    } catch (err) {
      console.error('❌ Error loading lab documents:', err);
      setError('Eroare la încărcarea documentelor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, [filters]);

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleFilterClear = () => {
    setFilters({
      animalId: '',
      type: '',
      fromDate: '',
      toDate: '',
      search: ''
    });
    setCurrentPage(1);
  };

  const handleDownload = async (documentId: string) => {
    try {
      // TODO: Implementează API call pentru download
      console.log('Downloading document:', documentId);
      
      // Simulare download
      const doc = documents.find(d => d.id === documentId);
      if (doc) {
        const link = document.createElement('a');
        link.href = doc.downloadUrl || '#';
        link.download = doc.fileName;
        link.click();
      }
    } catch (err) {
      console.error('Error downloading document:', err);
    }
  };

  const handlePreview = (documentId: string) => {
    // TODO: Implementează preview
    console.log('Previewing document:', documentId);
    navigate(`/analize/${documentId}`);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'sange': return <Bloodtype />;
      case 'urina': return <Science />;
      case 'eco': return <LocalHospital />;
      case 'radiografie': return <Image />;
      default: return <Description />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'sange': return 'error';
      case 'urina': return 'info';
      case 'eco': return 'success';
      case 'radiografie': return 'warning';
      default: return 'default';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'sange': return 'Analize de sânge';
      case 'urina': return 'Analize de urină';
      case 'eco': return 'Ecografie';
      case 'radiografie': return 'Radiografie';
      default: return 'Alt document';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const stats = {
    total: documents.length,
    byType: documents.reduce((acc, doc) => {
      acc[doc.type] = (acc[doc.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>)
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <SectionHeader
        title="Analize & Documente"
        subtitle="Rezultatele analizelor și documentele medicale ale animalelor tale"
        actions={{
          primary: {
            label: 'Încarcă document',
            icon: <Science />,
            onClick: () => {
              console.log('Upload document...');
              // TODO: Implementează upload
            }
          }
        }}
        badge={{
          count: stats.total,
          color: 'primary'
        }}
        onRefresh={loadDocuments}
        loading={loading}
      />

      {/* Statistici rapide */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary" gutterBottom>
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total documente
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        {Object.entries(stats.byType).map(([type, count]) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={type}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color={`${getTypeColor(type)}.main`} gutterBottom>
                  {count}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getTypeLabel(type)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filtre */}
      <FilterBar
        filters={filterOptions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleFilterClear}
        collapsible
      />

      {/* Lista documentelor */}
      <PaginatedList
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        loading={loading}
        error={error || undefined}
        emptyMessage="Nu există documente de analize"
      >
        <Grid container spacing={2}>
          {documents
            .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
            .map((document) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={document.id}>
                {/* <LabCard
                  document={document}
                  onDownload={() => handleDownload(document.id)}
                  onPreview={() => handlePreview(document.id)}
                /> */}
                <Card>
                  <CardContent>
                    <Typography variant="h6">{document.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {document.animalName} • {getTypeLabel(document.type)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
      </PaginatedList>
    </Container>
  );
};

export default LabsPage;
